import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { faqItems } from '@/mocks/faq';

function AccordionItem({ item, isOpen, toggle, index }: {
  item: typeof faqItems[0];
  isOpen: boolean;
  toggle: () => void;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className="border border-white/5 rounded-lg overflow-hidden"
    >
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between p-5 md:p-6 text-left bg-dark-800 hover:bg-dark-700/50 transition-colors duration-300"
      >
        <span className="text-white font-medium text-sm md:text-base pr-4">
          {item.question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className="w-8 h-8 rounded-full bg-premium-600/20 flex items-center justify-center flex-shrink-0"
        >
          <i className="ri-add-line text-premium-400"></i>
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="p-5 md:p-6 pt-0 md:pt-0 text-white/60 text-sm leading-relaxed">
              {item.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [openId, setOpenId] = useState<number | null>(null);

  const toggleItem = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section id="faq" className="relative py-24 md:py-32 lg:py-40 bg-dark-950 overflow-hidden">
      <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-premium-400 text-xs tracking-[0.3em] uppercase font-medium"
          >
            FAQ
          </motion.span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
            Perguntas{' '}
            <span className="text-gradient-gold">frequentes</span>
          </h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: '80px' } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="h-[1px] bg-premium-500 mx-auto mt-6"
          ></motion.div>
        </div>

        <div className="space-y-3">
          {faqItems.map((item, index) => (
            <AccordionItem
              key={item.id}
              item={item}
              isOpen={openId === item.id}
              toggle={() => toggleItem(item.id)}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}